import type { QueryKey, UseQueryOptions } from "@tanstack/react-query"

import {
  type GraphQLDocument,
  graphqlQueryKey,
  graphqlRequest,
  type GraphQLResponseError,
  retryGraphqlServerErrors,
} from "#Utils/graphqlClient"
import { queryOptions, useQuery } from "#Utils/react-query-client"

type MaybeVariables = Record<string, unknown> | undefined

type GraphQLQueryOptions<TData, TVariables, TSelectedData = TData> = Omit<
  UseQueryOptions<TData, GraphQLResponseError, TSelectedData, QueryKey>,
  "queryKey" | "queryFn"
> & {
  document: GraphQLDocument<TData, TVariables> | string
  variables?: TVariables
  operationName?: string
  queryKey?: QueryKey
}

export const graphqlQueryOptions = <TData, TVariables = MaybeVariables, TSelectedData = TData>({
  document,
  variables,
  operationName,
  queryKey = graphqlQueryKey(document, variables, operationName),
  retry = retryGraphqlServerErrors(3),
  ...options
}: GraphQLQueryOptions<TData, TVariables, TSelectedData>) => {
  return queryOptions({
    ...options,
    queryKey,
    retry,
    queryFn: ({ signal }) =>
      graphqlRequest<TData, TVariables>({ document, variables, operationName, signal }),
  })
}

export const useGraphqlQuery = <TData, TVariables = MaybeVariables, TSelectedData = TData>(
  query: GraphQLDocument<TData, TVariables> | string,
  options?: Omit<GraphQLQueryOptions<TData, TVariables, TSelectedData>, "document">,
) => {
  return useQuery(graphqlQueryOptions({ ...(options ?? {}), document: query }))
}
